import { IconMessageDots, IconSearch } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import {
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  type Selection,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Input } from "../Inputs/input.js";
import { skeletonStyle } from "../Skeletons/skeletons.js";

export interface ChatHistoryItem {
  id: string;
  title: string;
  /** Preformatted timestamp label (e.g. "Jul 24, 2026, 3:20 PM"). */
  timestamp: string;
}

export interface ChatHistorySection {
  id: string;
  /** Section label (e.g. "Today", "Last week"). */
  title: string;
  items: readonly ChatHistoryItem[];
}

export interface ChatHistoryProps {
  sections: readonly ChatHistorySection[];
  selectedItemId?: string;
  onItemSelect?: (itemId: string) => void;
  /**
   * Shows a search box. The search itself belongs to the surface: it owns
   * `query` and supplies already-matching `sections`, because filtering the
   * loaded pages of a paginated history would silently miss older chats.
   * {@link filterChatHistorySections} covers callers holding the whole list.
   */
  isSearchable?: boolean;
  /** Current search text. Required, with `onQueryChange`, when searchable. */
  query?: string;
  onQueryChange?: (query: string) => void;
  /** Shows skeleton rows in place of the sections. */
  isLoading?: boolean;
  /** Shown when there are no sections (and not loading or filtering). */
  emptyState?: ReactNode;
  /** Rendered after the sections (e.g. a "Load more" button). */
  footer?: ReactNode;
  className?: string;
}

const matchesSearch = (item: ChatHistoryItem, query: string) =>
  item.title.toLowerCase().includes(query.toLowerCase());

/**
 * Drops items whose title doesn't match `query`, and sections left empty by
 * that. For surfaces holding every chat in memory; paginated ones search
 * server-side instead.
 */
export function filterChatHistorySections(
  sections: readonly ChatHistorySection[],
  query: string,
): ChatHistorySection[] {
  const normalizedQuery = query.trim();
  if (normalizedQuery === "") {
    return [...sections];
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        matchesSearch(item, normalizedQuery),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * Grouped list of past chats. Purely presentational: the surface supplies
 * ready-made sections (grouping, pagination, searching, and data loading stay
 * outside).
 */
export const ChatHistory: FC<ChatHistoryProps> = ({
  sections,
  selectedItemId,
  onItemSelect,
  isSearchable = false,
  query = "",
  onQueryChange,
  isLoading = false,
  emptyState,
  footer,
  className,
}) => {
  const normalizedQuery = query.trim();

  const handleSelectionChange = (keys: Selection) => {
    // Single selection, so `keys` is never "all" and holds at most one key.
    const [selectedKey] = keys === "all" ? [] : [...keys];
    if (selectedKey !== undefined) {
      onItemSelect?.(String(selectedKey));
    }
  };

  return (
    <div className={twMerge("flex min-h-0 min-w-0 flex-col", className)}>
      {isSearchable ? (
        <Input
          aria-label="Search chats"
          value={query}
          onChange={(event) => onQueryChange?.(event.target.value)}
          placeholder="Search chats..."
          leftIcon={<IconSearch className="size-4 text-muted-foreground" />}
        />
      ) : null}

      {isLoading ? (
        <div
          className={twMerge(
            "flex flex-col gap-4",
            isSearchable ? "mt-4" : undefined,
          )}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className={skeletonStyle({ className: "h-12 rounded-lg" })}
            />
          ))}
        </div>
      ) : sections.length > 0 ? (
        // One listbox rather than a button per chat: the whole history is a
        // single tab stop, and arrow keys, Home/End and typeahead move between
        // chats. Selection is `toggle`, so arrowing through the list moves
        // focus without opening every chat it passes.
        <ListBox
          aria-label="Chat history"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={selectedItemId === undefined ? [] : [selectedItemId]}
          onSelectionChange={handleSelectionChange}
          className={twMerge(
            "flex flex-col gap-5 outline-none",
            isSearchable ? "mt-4" : undefined,
          )}
        >
          {sections.map((section) => (
            <ListBoxSection
              key={section.id}
              id={section.id}
              className="flex flex-col gap-0.5"
            >
              <Header className="mb-1.5 text-xs font-semibold text-muted-foreground">
                {section.title}
              </Header>
              {section.items.map((item) => (
                <ListBoxItem
                  key={item.id}
                  id={item.id}
                  textValue={item.title}
                  // A ring rather than the `data-[focused]:bg-muted` the other
                  // collections here use, because `bg-muted` already means
                  // "selected" and arrowing has to stay distinguishable from
                  // the chat that is open.
                  //
                  // `[&:focus-visible]` rather than the `focus-visible:`
                  // variant: `tailwindcss-react-aria-components` rewrites that
                  // variant to React Aria's `[data-focus-visible]` on any
                  // `data-rac` element, and that attribute tracks the current
                  // input modality — a row re-renders on hover, re-evaluates,
                  // and drops the ring the moment the pointer moves. The
                  // native pseudo-class keeps the ring off for mouse clicks
                  // without losing it on hover.
                  //
                  // Only the background transitions: Tailwind's
                  // `transition-colors` covers `outline-color` too, which
                  // makes the ring fade in from black before reaching indigo.
                  className="flex w-full cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-[background-color] hover:bg-muted data-[selected]:bg-muted [&:focus-visible]:outline-2 [&:focus-visible]:outline-offset-2 [&:focus-visible]:outline-primary"
                >
                  <IconMessageDots className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {item.timestamp}
                    </span>
                  </span>
                </ListBoxItem>
              ))}
            </ListBoxSection>
          ))}
        </ListBox>
      ) : normalizedQuery !== "" ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
          <p className="text-sm font-medium text-muted-foreground">
            No chats match your search.
          </p>
        </div>
      ) : (
        emptyState
      )}

      {footer}
    </div>
  );
};
